import { lazy } from "react";
import { Router, Switch, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { RecoilRoot } from "recoil";
import {
  ApolloClient,
  createHttpLink,
  fromPromise,
  InMemoryCache,
  ApolloProvider,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

import { cacheOptions } from "./MVVM/GraphQL/cacheOptions";
import {
  forceLogout,
  isAuthError,
  isClientSideLoggedIn,
  tryRefreshToken,
} from "./MVVM/Utilities/Auth";

import { Main } from "./main";
import { getChunkable } from "./routes/utils";
import history from "./history";
import theme from "./theme/themeThree";
import { UserStatePreFill } from "./MVVM/Hooks/userStatePreFill";
import { GlobalStyles } from "./theme/globalStyles";
import { AdsProvider } from "./MVVM/Providers";
import { GamePreferenceInitializer } from "MVVM/Providers/GamePreferenceInitializer";
import { PageViewTracker } from "MVVM/Analytics";
import { ShareCodeRoller } from "MVVM/Hooks/useShareCodeRoller";

const { REACT_APP_GQL_ENDPOINT } = process.env;

const LazyEmbeddablePlayer = lazy(() =>
  import("./MVVM/Views/EmbeddablePlayer"),
);
const LazyEmbeddablePlaylist = lazy(() =>
  import("./MVVM/Views/EmbeddablePlaylist"),
);
// Mounted at the top level (above <Main />) so it bypasses the Allstar app
// chrome — the page IS the host site mock, no nav/sidebar should bleed
// through.
const LazyHLTVPreviewDemo = lazy(() => import("./MVVM/Views/HLTVPreviewDemo"));
// Bookmarklet install page — drag-to-bookmarks-bar UX for live HLTV
// iframe injection. Top-level for the same standalone reason.
const LazyHLTVBookmarklet = lazy(() => import("./MVVM/Views/HLTVBookmarklet"));

const IframeComponent = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const playlist = searchParams.get("playlist");

  // EmbeddablePlayer renders one clip by ?clip=; EmbeddablePlaylist routes partner
  // embeds by platform. The Highlights tab is the latter even with no playlist param,
  // since it resolves its own clips from matchId/riotID.
  const layout = searchParams.get("layout") || "";
  const content =
    playlist || layout === "highlights-tab" ? (
      <LazyEmbeddablePlaylist playlist={playlist || ""} />
    ) : (
      <LazyEmbeddablePlayer />
    );

  return content;
};

const SKIP_AUTH_RETRY_OPS = ["LOGIN", "SIGNUP", "Login", "Signup"];

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    const isAuthenticatedRequest =
      isClientSideLoggedIn() &&
      !SKIP_AUTH_RETRY_OPS.includes(operation.operationName);

    if (isAuthError(graphQLErrors, networkError) && isAuthenticatedRequest) {
      if (operation.getContext()._authRetried) {
        forceLogout("SESSION EXPIRED");
        return;
      }

      return fromPromise(tryRefreshToken())
        .filter((newToken) => {
          if (!newToken) {
            forceLogout("SESSION EXPIRED");
            return false;
          }
          return true;
        })
        .flatMap((newToken) => {
          operation.setContext(({ headers = {} }) => ({
            _authRetried: true,
            headers: { ...headers, authorization: `Bearer ${newToken}` },
          }));
          return forward(operation);
        });
    }

    if (graphQLErrors)
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        ),
      );
    if (networkError) console.log(`[Network error]: ${networkError}`);
  },
);

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem("token");
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      authorization: token ? `Bearer ${token}` : "",
      ...headers,
    },
  };
});

const httpLink = createHttpLink({
  uri: REACT_APP_GQL_ENDPOINT,
});

const apolloClient = new ApolloClient({
  cache: new InMemoryCache(cacheOptions),
  link: authLink.concat(errorLink).concat(httpLink),
});

export default function () {
  return (
    <ApolloProvider client={apolloClient}>
      <RecoilRoot id="root">
        <AdsProvider>
          <ThemeProvider theme={theme}>
            <GlobalStyles />
            <Router history={history}>
              <PageViewTracker />
              <GamePreferenceInitializer />
              <UserStatePreFill force={false} />
              <ShareCodeRoller />
              <Switch>
                <Route exact path="/iframe" component={IframeComponent} />
                <Route
                  exact
                  path="/demo/hltv-preview"
                  render={getChunkable(LazyHLTVPreviewDemo)}
                />
                <Route
                  exact
                  path="/demo/hltv-bookmarklet"
                  render={getChunkable(LazyHLTVBookmarklet)}
                />
                <Main />
              </Switch>
            </Router>
          </ThemeProvider>
        </AdsProvider>
      </RecoilRoot>
    </ApolloProvider>
  );
}
